/* ──────────────────────────────────────────────────────────────
   src/pages/reports/reservas/RetrasosReservasPage.tsx
   ────────────────────────────────────────────────────────────── */
import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import {
  Stack,
  TextField,
  Button,
  Paper,
  LinearProgress,
  Alert,
  Typography,
  Box,
  Chip,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import { useRetrasosReservasReport } from '../../../hooks/useReportJob';
import type {
  RetrasosReservaEvolRow,
  RetrasosReservaDistribRow,
  RetrasosReservaGlobalRow,
} from '../../../api/reports';
import { downloadReportFile } from '../../../components/utils/downloadXlsx';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

/* ──────────────────────────────────────────────────────────────
   Helpers de formato
   ────────────────────────────────────────────────────────────── */
const fmtInt = (n: number) => (Number.isFinite(n) ? n.toLocaleString() : '—');
const fmt1 = (n: number) =>
  Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—';

const formatMinutos = (min: number) => {
  const horas = (Number(min) || 0) / 60;
  return horas.toLocaleString('en-US', {
    useGrouping: false,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const fmtDate = (s?: string) => {
  if (!s) return '—';
  const d = dayjs(s);
  return d.isValid() ? d.format('DD/MM') : s;
};

/* ──────────────────────────────────────────────────────────────
   Tooltips personalizados (Recharts)
   ────────────────────────────────────────────────────────────── */
function LineEvolucionTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5 }}>
      <Typography variant="subtitle2">{fmtDate(label)}</Typography>
      {payload.map((p: any, i: number) => (
        <Typography key={i} variant="body2" sx={{ color: p.color }}>
          {p.name}: <b>{p.value}</b>
        </Typography>
      ))}
    </Paper>
  );
}

function BarDistribTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <Paper sx={{ p: 1.5 }}>
      <Typography variant="subtitle2">{p.name}</Typography>
      <Typography variant="body2">
        {p.dataKey === 'numRetrasos'
          ? `Retrasos: ${fmtInt(p.value)}`
          : `Tiempo: ${formatMinutos(p.value)}`}
      </Typography>
    </Paper>
  );
}

export default function RetrasosReservasPage() {
  const { t } = useTranslation<'translation'>();
  const today = dayjs().format('YYYY-MM-DD');
  const [desde, setDesde] = useState(today);
  const [hasta, setHasta] = useState(today);

  const [jobId, setJobId] = useState<string>();
  const { run, useStatus } = useRetrasosReservasReport();
  const { data: status } = useStatus(jobId);

  const launch = async () => {
    const { jobId } = await run({ desde, hasta });
    setJobId(jobId);
  };

  const handleDownload = async () => {
    if (status?.result?.url) {
      try {
        await downloadReportFile(status.result.url);
      } catch (error) {
        console.error('Error al descargar:', error);
      }
    }
  };

  const data = status?.result;

  /* ─── datos procesados para gráficas ─── */
  const chartData = useMemo(() => {
    if (!data?.evolucion) return [];
    return data.evolucion.map((r: RetrasosReservaEvolRow) => ({
      fecha: r.fecha,
      'Retrasos Profesor': r.numRetrasosProf,
      'Retrasos Alumno': r.numRetrasosAlu,
      'Tiempo Prof (min)': r.tiempoTotalProf,
      'Tiempo Alu (min)': r.tiempoTotalAlu,
    }));
  }, [data]);

  const distribProf = useMemo(() => {
    if (!data?.distribucion) return [];
    return data.distribucion
      .filter((r: RetrasosReservaDistribRow) => r.tipo === 'profesor')
      .slice(0, 10)
      .map((r: RetrasosReservaDistribRow) => ({
        nombre: r.nombre,
        numRetrasos: r.numRetrasos,
        tiempoTotalMin: r.tiempoTotalMin,
      }));
  }, [data]);

  const distribAlu = useMemo(() => {
    if (!data?.distribucion) return [];
    return data.distribucion
      .filter((r: RetrasosReservaDistribRow) => r.tipo === 'alumno')
      .slice(0, 10)
      .map((r: RetrasosReservaDistribRow) => ({
        nombre: r.nombre,
        numRetrasos: r.numRetrasos,
        tiempoTotalMin: r.tiempoTotalMin,
      }));
  }, [data]);

  const globalesData = useMemo(() => {
    if (!data?.globales) return [];
    return data.globales.map((r: RetrasosReservaGlobalRow) => ({
      name: r.tipo === 'profesor' ? 'Profesor' : 'Alumno',
      value: r.numRetrasos,
      tiempoTotal: r.tiempoTotalMin,
      tasa: r.tasaRetrasos,
    }));
  }, [data]);

  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t('retrasosReservas.titulo', 'Retrasos en Reservas')}</Typography>

      {/* ─── barra de filtros ─── */}
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <TextField
          type="date"
          label={t('retrasosReservas.filtros.desde', 'Desde')}
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          type="date"
          label={t('retrasosReservas.filtros.hasta', 'Hasta')}
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="contained"
          onClick={launch}
          disabled={!desde || !hasta || (status?.progress === 100 && !status.done)}
          startIcon={<SearchIcon />}
        >
          {t('retrasosReservas.generar', 'Generar Informe')}
        </Button>
      </Stack>

      {/* ─── progreso ─── */}
      {status && !status.done && !status.failed && (
        <Box>
          <LinearProgress variant="determinate" value={status.progress} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {t('retrasosReservas.procesando', 'Procesando...')} {status.progress}%
          </Typography>
        </Box>
      )}

      {/* ─── error ─── */}
      {status?.failed && (
        <Alert severity="error">
          {t(
            'retrasosReservas.error',
            'Error al generar el informe. Por favor, inténtalo de nuevo.',
          )}
        </Alert>
      )}

      {/* ─── resultados ─── */}
      {status?.done && data && (
        <Stack spacing={3}>
          {/* ─── resumen ─── */}
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                {t('retrasosReservas.resultados', 'Resultados')}
              </Typography>
              {status.result?.url && (
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                  variant="outlined"
                >
                  {t('retrasosReservas.descargar', 'Descargar Excel')}
                </Button>
              )}
            </Stack>
          </Paper>

          {/* ─── tarjetas resumen global ─── */}
          <Grid container spacing={2}>
            {data.globales.map((g: RetrasosReservaGlobalRow) => (
              <Grid item xs={12} md={6} key={g.tipo}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {g.tipo === 'profesor' ? 'Profesores' : 'Alumnos'}
                    </Typography>
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Total Retrasos:
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {fmtInt(g.numRetrasos)}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Tiempo Total:
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {formatMinutos(g.tiempoTotalMin)}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Promedio:
                        </Typography>
                        <Typography variant="body2">
                          {formatMinutos(Math.round(g.tiempoPromedioMin))}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Tasa Retrasos:
                        </Typography>
                        <Chip
                          label={`${g.tasaRetrasos.toFixed(1)}%`}
                          size="small"
                          color={
                            g.tasaRetrasos >= 20
                              ? 'error'
                              : g.tasaRetrasos >= 10
                                ? 'warning'
                                : 'success'
                          }
                        />
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* ─── evolución temporal ─── */}
          {chartData.length > 0 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t('retrasosReservas.evolucion', 'Evolución Temporal')}
              </Typography>
              <Box sx={{ width: '100%', height: 300, mt: 2 }}>
                <ResponsiveContainer>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="fecha"
                      tickFormatter={fmtDate}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis />
                    <RechartsTooltip content={<LineEvolucionTooltip />} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="Retrasos Profesor"
                      stroke="#0088FE"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="Retrasos Alumno"
                      stroke="#00C49F"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          )}

          {/* ─── distribución top profesores ─── */}
          {distribProf.length > 0 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t('retrasosReservas.topProfesores', 'Top 10 Profesores con más Retrasos')}
              </Typography>
              <Box sx={{ width: '100%', height: 300, mt: 2 }}>
                <ResponsiveContainer>
                  <BarChart data={distribProf} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="nombre" type="category" width={120} />
                    <RechartsTooltip content={<BarDistribTooltip />} />
                    <Bar dataKey="numRetrasos" fill="#0088FE" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          )}

          {/* ─── distribución top alumnos ─── */}
          {distribAlu.length > 0 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t('retrasosReservas.topAlumnos', 'Top 10 Alumnos con más Retrasos')}
              </Typography>
              <Box sx={{ width: '100%', height: 300, mt: 2 }}>
                <ResponsiveContainer>
                  <BarChart data={distribAlu} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="nombre" type="category" width={120} />
                    <RechartsTooltip content={<BarDistribTooltip />} />
                    <Bar dataKey="numRetrasos" fill="#00C49F" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          )}
        </Stack>
      )}
    </Stack>
  );
}
